import React, { useState, useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import {
  Star,
  MousePointerClick,
  X,
  FileText,
  Workflow,
  MessageSquare,
  ImageIcon,
  Plus,
  Trash2,
  Table2,
} from 'lucide-react';
import { EditorToolbar } from './EditorToolbar';
import WidgetMarkdownPreview from './WidgetMarkdownPreview';

// ─── Shared Dialog Shell ────────────────────────────────────────────────────
interface DialogShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  width?: string;
}

const DialogShell: React.FC<DialogShellProps> = ({ isOpen, onClose, title, icon, children, width = 'w-96' }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className={`bg-white  p-5 ${width} shadow-xl max-h-[85vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center">
            {icon}
            <span className="ml-2">{title}</span>
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

const InputField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
}> = ({ label, value, onChange, placeholder, type = 'text', required }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-1.5 text-sm border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
    />
  </div>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-1.5 text-sm border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const DialogActions: React.FC<{ onClose: () => void; onInsert: () => void; insertLabel?: string }> = ({
  onClose,
  onInsert,
  insertLabel = 'Insert',
}) => (
  <div className="flex justify-end space-x-2 pt-3">
    <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100  hover:bg-gray-200">
      Cancel
    </button>
    <button type="button" onClick={onInsert} className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600  hover:bg-indigo-700">
      {insertLabel}
    </button>
  </div>
);

// ─── Star Rating Dialog ─────────────────────────────────────────────────────
export interface StarRatingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const StarRatingDialog: React.FC<StarRatingDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [rating, setRating] = useState(5);

  const handleInsert = () => {
    const stars = '⭐'.repeat(Math.max(1, Math.min(5, rating)));
    onInsert(stars);
    onClose();
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Star Rating" icon={<Star className="h-4 w-4 text-yellow-500" />} width="w-80">
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Rating (1-5 stars)</label>
          <div className="flex items-center space-x-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className={`text-2xl transition-transform ${n <= rating ? 'scale-110' : 'opacity-30 grayscale'}`}
              >
                ⭐
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">{rating} star{rating !== 1 ? 's' : ''}</p>
        </div>
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── CTA Dialog ─────────────────────────────────────────────────────────────
export interface CTADialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const CTADialog: React.FC<CTADialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [buttonText, setButtonText] = useState('Click Here');
  const [url, setUrl] = useState('');
  const [contentType, setContentType] = useState('cta_generic');
  const [ctaId, setCtaId] = useState('');
  const [title, setTitle] = useState('');
  const [target, setTarget] = useState('_self');

  const handleInsert = () => {
    const effectiveUrl = contentType === 'cta_interactive_flow' ? '#' : url;
    if (!buttonText || (!effectiveUrl && contentType !== 'cta_interactive_flow')) return;

    // Build: [text](url)(type, id, "title", target)
    let parts = [contentType];
    if (ctaId) parts.push(ctaId);
    if (title) parts.push(`"${title}"`);
    if (target === '_blank' && contentType !== 'cta_interactive_flow') parts.push('_blank');

    const raw = `[${buttonText}](${effectiveUrl})(${parts.join(', ')})`;
    onInsert(raw);
    onClose();
    // Reset
    setButtonText('Click Here');
    setUrl('');
    setContentType('cta_generic');
    setCtaId('');
    setTitle('');
    setTarget('_self');
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert CTA Button" icon={<MousePointerClick className="h-4 w-4 text-indigo-500" />}>
      <div className="space-y-3">
        <InputField label="Button Text" value={buttonText} onChange={setButtonText} required />
        <SelectField
          label="CTA Type"
          value={contentType}
          onChange={setContentType}
          options={[
            { value: 'cta_generic', label: 'Generic' },
            { value: 'cta_full', label: 'Full Width' },
            { value: 'cta_interactive_flow', label: 'Interactive Flow' },
          ]}
        />
        {contentType !== 'cta_interactive_flow' ? (
          <InputField label="URL" value={url} onChange={setUrl} placeholder="https://..." type="url" required />
        ) : null}
        <InputField
          label={contentType === 'cta_interactive_flow' ? 'Flow ID' : 'CTA ID (optional)'}
          value={ctaId}
          onChange={setCtaId}
          placeholder={contentType === 'cta_interactive_flow' ? 'flowId123' : 'Optional identifier'}
          required={contentType === 'cta_interactive_flow'}
        />
        <InputField label="Title (optional)" value={title} onChange={setTitle} placeholder="Displayed above button" />
        {contentType !== 'cta_interactive_flow' && (
          <SelectField
            label="Open In"
            value={target}
            onChange={setTarget}
            options={[
              { value: '_self', label: 'Same tab' },
              { value: '_blank', label: 'New tab' },
            ]}
          />
        )}
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Logo Dialog ────────────────────────────────────────────────────────────
export interface LogoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const LogoDialog: React.FC<LogoDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [altText, setAltText] = useState('Logo');
  const [imageUrl, setImageUrl] = useState('');
  const [shape, setShape] = useState('logo_circle');

  const handleInsert = () => {
    if (!imageUrl) return;
    const raw = `[${altText}](${imageUrl})(${shape})`;
    onInsert(raw);
    onClose();
    setAltText('Logo');
    setImageUrl('');
    setShape('logo_circle');
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Logo" icon={<ImageIcon className="h-4 w-4 text-green-600" />} width="w-80">
      <div className="space-y-3">
        <InputField label="Alt Text" value={altText} onChange={setAltText} />
        <InputField label="Image URL" value={imageUrl} onChange={setImageUrl} placeholder="https://..." type="url" required />
        <SelectField
          label="Shape"
          value={shape}
          onChange={setShape}
          options={[
            { value: 'logo_circle', label: 'Circle' },
            { value: 'logo_square', label: 'Square' },
            { value: 'logo_original', label: 'Original' },
          ]}
        />
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Interactive Flow Dialog ────────────────────────────────────────────────
export interface InteractiveFlowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const InteractiveFlowDialog: React.FC<InteractiveFlowDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [flowId, setFlowId] = useState('');

  const handleInsert = () => {
    if (!flowId.trim()) return;
    const raw = `<interactive-flow flow_id='${flowId.trim()}' />`;
    onInsert(raw);
    onClose();
    setFlowId('');
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Interactive Flow" icon={<Workflow className="h-4 w-4 text-purple-500" />} width="w-80">
      <div className="space-y-3">
        <InputField label="Flow ID" value={flowId} onChange={setFlowId} placeholder="e.g. abc-123" required />
        <p className="text-xs text-gray-500">The widget will render the interactive flow with this ID.</p>
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Google Reviews Dialog ──────────────────────────────────────────────────
export interface GoogleReviewsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const GoogleReviewsDialog: React.FC<GoogleReviewsDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [count, setCount] = useState('5');

  const handleInsert = () => {
    const c = Math.max(1, Math.min(10, parseInt(count) || 5));
    const raw = `<RandomGoogleReviews count="${c}" />`;
    onInsert(raw);
    onClose();
    setCount('5');
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Google Reviews" icon={<MessageSquare className="h-4 w-4 text-indigo-500" />} width="w-80">
      <div className="space-y-3">
        <InputField label="Number of Reviews (1-10)" value={count} onChange={setCount} type="number" />
        <p className="text-xs text-gray-500">Displays random Google reviews from your business.</p>
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Media Carousel Dialog ──────────────────────────────────────────────────
export interface MediaCarouselDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const MediaCarouselDialog: React.FC<MediaCarouselDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [items, setItems] = useState([{ alt: '', url: '' }]);

  const addItem = () => setItems([...items, { alt: '', url: '' }]);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, field: 'alt' | 'url', value: string) => {
    const updated = [...items];
    updated[i][field] = value;
    setItems(updated);
  };

  const handleInsert = () => {
    const validItems = items.filter((item) => item.url.trim());
    if (validItems.length === 0) return;
    const mediaItems = validItems.map((item) => `<media-item>![${item.alt}](${item.url})</media-item>`).join('\n');
    const raw = `<carousel>\n${mediaItems}\n</carousel>`;
    onInsert(raw);
    onClose();
    setItems([{ alt: '', url: '' }]);
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Media Carousel" icon={<ImageIcon className="h-4 w-4 text-orange-500" />} width="w-[480px]">
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-start space-x-2 p-2 bg-gray-50">
            <div className="flex-1 space-y-1">
              <input
                type="url"
                value={item.url}
                onChange={(e) => updateItem(i, 'url', e.target.value)}
                placeholder="Media URL..."
                className="w-full px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              <input
                type="text"
                value={item.alt}
                onChange={(e) => updateItem(i, 'alt', e.target.value)}
                placeholder="Alt text (optional)"
                className="w-full px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            {items.length > 1 && (
              <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 mt-1">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={addItem} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center">
          <Plus className="h-3 w-3 mr-1" /> Add media item
        </button>
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── SimpleEditor ──────────────────────────────────────────────────────────
// Self-contained tiptap editor with a minimal toolbar.
// Lives here (not in RichTextEditor.tsx) to avoid a circular import:
//   InsertDialogs ← RichTextEditor ← InsertDialogs
//
// Toolbar shown: Bold · Italic · Strike · H1/H2/H3 · Link · Raw/Preview
// Toolbar hidden: Undo/Redo · Lists · Blockquote · Code · Insert dropdown
// ───────────────────────────────────────────────────────────────────────────

interface SimpleEditorProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
}

const SimpleEditor: React.FC<SimpleEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write item content…',
}) => {
  // Start in raw mode by default — matches the full RichTextEditor default,
  // ensures toolbar actions (e.g. Insert Image) always use the raw string path.
  const [isRawMode, setIsRawMode] = useState(true);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [rawCursorPos, setRawCursorPos] = useState(0);
  const [rawSelEnd, setRawSelEnd] = useState(0);
  const rawTextareaRef = useRef<HTMLTextAreaElement>(null);
  const isInternalChange = useRef(false);
  const isSwitchingToRich = useRef(false);
  const lastExternalValue = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-indigo-600 underline hover:text-indigo-800' },
      }),
      Placeholder.configure({ placeholder }),
      Markdown.configure({ html: true, transformPastedText: true }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      if (isSwitchingToRich.current) return;
      const storage = editor.storage as any;
      const markdown = storage.markdown?.getMarkdown?.() ?? editor.getHTML();
      isInternalChange.current = true;
      lastExternalValue.current = markdown;
      onChange(markdown);
    },
    editorProps: {
      attributes: { class: 'prose prose-sm max-w-none focus:outline-none px-3 py-2' },
    },
  });

  // Sync external value changes (e.g. dialog reset) into tiptap
  React.useEffect(() => {
    if (!editor || isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      isSwitchingToRich.current = true;
      editor.commands.setContent(value);
      requestAnimationFrame(() => { isSwitchingToRich.current = false; });
    }
  }, [value, editor]);

  const updateRawCursor = () => {
    const ta = rawTextareaRef.current;
    if (ta) { setRawCursorPos(ta.selectionStart); setRawSelEnd(ta.selectionEnd); }
  };

  const handleToggleRaw = () => {
    if (isRawMode && editor) {
      isSwitchingToRich.current = true;
      isInternalChange.current = true;
      lastExternalValue.current = value;
      editor.commands.setContent(value);
      requestAnimationFrame(() => { isSwitchingToRich.current = false; });
    }
    setIsRawMode((m) => !m);
  };

  const handleInsertRaw = (raw: string) => {
    const ta = rawTextareaRef.current;
    if (isRawMode && ta) {
      // Raw mode: insert at cursor position in the textarea.
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newVal = value.substring(0, start) + raw + value.substring(end);
      isInternalChange.current = true;
      lastExternalValue.current = newVal;
      onChange(newVal);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + raw.length; ta.focus(); });
    } else {
      // Rich mode: flush tiptap's current content, append raw string, then switch
      // to raw mode — exactly how insertRawText() behaves for widget markup in the
      // full RichTextEditor. Never call editor.insertContent() because tiptap-markdown
      // will convert markdown syntax (e.g. ![image](url)) to HTML.
      const storage = editor?.storage as any;
      const richContent = editor ? (storage.markdown?.getMarkdown?.() ?? value) : value;
      const sep = richContent && !richContent.endsWith('\n') ? '\n' : '';
      const newVal = richContent + sep + raw;
      isInternalChange.current = true;
      lastExternalValue.current = newVal;
      onChange(newVal);
      setIsRawMode(true);
    }
  };

  if (!editor) return null;

  return (
    <div className="border border-gray-300 overflow-hidden focus-within:ring-1 focus-within:ring-indigo-500 focus-within:border-indigo-500">
      <EditorToolbar
        editor={editor}
        onOpenDialog={() => {/* no insert dialogs inside SimpleEditor */ }}
        onInsertRaw={handleInsertRaw}
        isRawMode={isRawMode}
        onToggleRawMode={handleToggleRaw}
        isPreviewMode={isPreviewMode}
        onTogglePreview={() => setIsPreviewMode((p) => !p)}
        rawTextareaRef={rawTextareaRef}
        rawValue={value}
        onRawChange={(newVal) => {
          isInternalChange.current = true;
          lastExternalValue.current = newVal;
          onChange(newVal);
          setTimeout(updateRawCursor, 0);
        }}
        rawCursorPos={rawCursorPos}
        rawSelEnd={rawSelEnd}
        toolbarVariant="minimal"
      />
      {/* Resizable content area — user can drag the bottom edge to expand */}
      <div style={{ minHeight: '80px', resize: 'vertical', overflow: 'auto' }}>
        {isPreviewMode ? (
          // Preview: identical to the full RichTextEditor's.
          <div className="w-full h-full p-3 bg-white overflow-auto min-h-[80px]">
            <WidgetMarkdownPreview value={value} />
          </div>
        ) : isRawMode ? (
          <textarea
            ref={rawTextareaRef}
            value={value}
            onChange={(e) => {
              isInternalChange.current = true;
              lastExternalValue.current = e.target.value;
              onChange(e.target.value);
              setRawCursorPos(e.target.selectionStart);
            }}
            onKeyUp={updateRawCursor}
            onMouseUp={updateRawCursor}
            onSelect={updateRawCursor}
            placeholder={placeholder}
            className="w-full h-full min-h-[80px] px-3 py-2 text-sm font-mono text-gray-800 resize-none focus:outline-none bg-gray-50"
            spellCheck={false}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>
    </div>
  );
};

// ─── Item Carousel Dialog ───────────────────────────────────────────────────
export interface ItemCarouselDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const ItemCarouselDialog: React.FC<ItemCarouselDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [items, setItems] = useState(['']);

  const addItem = () => setItems([...items, '']);
  const removeItem = (i: number) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, value: string) => {
    const updated = [...items];
    updated[i] = value;
    setItems(updated);
  };

  // Reset all item editors to empty whenever the dialog is dismissed without inserting.
  const handleClose = () => {
    setItems(['']);
    onClose();
  };

  const handleInsert = () => {
    const validItems = items.filter((item) => item.trim());
    if (validItems.length === 0) return;
    const carouselItems = validItems.map((item) => `<item>${item}</item>`).join('\n');
    const raw = `<carousel>\n${carouselItems}\n</carousel>`;
    onInsert(raw);
    handleClose();
  };

  return (
    <DialogShell isOpen={isOpen} onClose={handleClose} title="Insert Item Carousel" icon={<FileText className="h-4 w-4 text-teal-500" />} width="w-[600px]">
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Item {i + 1}</span>
              {items.length > 1 && (
                <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <SimpleEditor
              value={item}
              onChange={(val) => updateItem(i, val)}
              placeholder={`Item ${i + 1} content…`}
            />
          </div>
        ))}
        <button type="button" onClick={addItem} className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center">
          <Plus className="h-3 w-3 mr-1" /> Add item
        </button>
        <DialogActions onClose={handleClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Dynamic Form Dialog ────────────────────────────────────────────────────
interface FormField {
  type: string;
  id: string;
  label: string;
  placeholder: string;
  required: boolean;
  options: string;
}

export interface FormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const FormDialog: React.FC<FormDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [formTitle, setFormTitle] = useState('');
  const [submitText, setSubmitText] = useState('Submit');
  const [fields, setFields] = useState<FormField[]>([
    { type: 'text', id: 'name', label: 'Name', placeholder: '', required: true, options: '' },
  ]);

  const fieldTypes = ['text', 'email', 'phone', 'textarea', 'select', 'checkbox', 'radio', 'number'];

  const addField = () =>
    setFields([...fields, { type: 'text', id: '', label: '', placeholder: '', required: false, options: '' }]);
  const removeField = (i: number) => setFields(fields.filter((_, idx) => idx !== i));
  const updateField = (i: number, key: keyof FormField, value: any) => {
    const updated = [...fields];
    updated[i] = { ...updated[i], [key]: value };
    setFields(updated);
  };

  const handleInsert = () => {
    const validFields = fields.filter((f) => f.id && f.label);
    if (validFields.length === 0) return;

    const titleAttr = formTitle ? ` title="${formTitle}"` : '';
    const submitAttr = submitText && submitText !== 'Submit' ? ` submit-text="${submitText}"` : '';

    const fieldTags = validFields
      .map((f) => {
        let attrs = `type="${f.type}" id="${f.id}" label="${f.label}"`;
        if (f.placeholder) attrs += ` placeholder="${f.placeholder}"`;
        if (f.required) attrs += ` required`;
        if (f.options && ['select', 'radio'].includes(f.type)) attrs += ` options="${f.options}"`;
        return `<field ${attrs} />`;
      })
      .join('\n');

    const raw = `<form${titleAttr}${submitAttr}>\n${fieldTags}\n</form>`;
    onInsert(raw);
    onClose();
    setFormTitle('');
    setSubmitText('Submit');
    setFields([{ type: 'text', id: 'name', label: 'Name', placeholder: '', required: true, options: '' }]);
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Form" icon={<FileText className="h-4 w-4 text-green-600" />} width="w-[560px]">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <InputField label="Form Title" value={formTitle} onChange={setFormTitle} placeholder="Contact Us" />
          <InputField label="Submit Button Text" value={submitText} onChange={setSubmitText} />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Fields</label>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {fields.map((field, i) => (
              <div key={i} className="p-2 bg-gray-50  border border-gray-200 space-y-1.5">
                <div className="grid grid-cols-3 gap-1.5">
                  <select
                    value={field.type}
                    onChange={(e) => updateField(i, 'type', e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fieldTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <input
                    value={field.id}
                    onChange={(e) => updateField(i, 'id', e.target.value)}
                    placeholder="Field ID"
                    className="px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <input
                    value={field.label}
                    onChange={(e) => updateField(i, 'label', e.target.value)}
                    placeholder="Label"
                    className="px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    value={field.placeholder}
                    onChange={(e) => updateField(i, 'placeholder', e.target.value)}
                    placeholder="Placeholder (optional)"
                    className="flex-1 px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  <label className="flex items-center text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(i, 'required', e.target.checked)}
                      className="mr-1"
                    />
                    Req
                  </label>
                  {fields.length > 1 && (
                    <button type="button" onClick={() => removeField(i)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {['select', 'radio'].includes(field.type) && (
                  <input
                    value={field.options}
                    onChange={(e) => updateField(i, 'options', e.target.value)}
                    placeholder="Options (comma-separated)"
                    className="w-full px-2 py-1 text-xs border border-gray-300  focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addField} className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center">
            <Plus className="h-3 w-3 mr-1" /> Add field
          </button>
        </div>

        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Single Form Dialog ─────────────────────────────────────────────────────
export interface SingleFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const SingleFormDialog: React.FC<SingleFormDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [fieldId, setFieldId] = useState('');
  const [fieldType, setFieldType] = useState('email');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldPlaceholder, setFieldPlaceholder] = useState('');
  const [title, setTitle] = useState('');
  const [submitText, setSubmitText] = useState('Submit');

  const handleInsert = () => {
    if (!fieldId.trim()) return;
    let attrs = `field_id="${fieldId}" field_type="${fieldType}"`;
    if (fieldLabel) attrs += ` field_label="${fieldLabel}"`;
    if (fieldPlaceholder) attrs += ` field_placeholder="${fieldPlaceholder}"`;
    if (title) attrs += ` title="${title}"`;
    if (submitText && submitText !== 'Submit') attrs += ` submit_button_text="${submitText}"`;

    const raw = `<single-form ${attrs} />`;
    onInsert(raw);
    onClose();
    setFieldId('');
    setFieldType('email');
    setFieldLabel('');
    setFieldPlaceholder('');
    setTitle('');
    setSubmitText('Submit');
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Single Form" icon={<FileText className="h-4 w-4 text-cyan-600" />}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <InputField label="Field ID" value={fieldId} onChange={setFieldId} placeholder="email" required />
          <SelectField
            label="Field Type"
            value={fieldType}
            onChange={setFieldType}
            options={[
              { value: 'text', label: 'Text' },
              { value: 'email', label: 'Email' },
              { value: 'phone', label: 'Phone' },
              { value: 'number', label: 'Number' },
              { value: 'textarea', label: 'Textarea' },
            ]}
          />
        </div>
        <InputField label="Label" value={fieldLabel} onChange={setFieldLabel} placeholder="Email Address" />
        <InputField label="Placeholder" value={fieldPlaceholder} onChange={setFieldPlaceholder} placeholder="you@example.com" />
        <InputField label="Title (optional)" value={title} onChange={setTitle} placeholder="Subscribe" />
        <InputField label="Submit Button Text" value={submitText} onChange={setSubmitText} />
        <DialogActions onClose={onClose} onInsert={handleInsert} />
      </div>
    </DialogShell>
  );
};

// ─── Table Dialog ───────────────────────────────────────────────────────────
export interface TableDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (raw: string) => void;
}

export const TableDialog: React.FC<TableDialogProps> = ({ isOpen, onClose, onInsert }) => {
  const [rows, setRows] = useState(3);
  const [cols, setCols] = useState(3);
  const [cells, setCells] = useState<string[][]>(() => buildGrid(3, 3));

  function buildGrid(r: number, c: number): string[][] {
    return Array.from({ length: r }, (_, ri) =>
      Array.from({ length: c }, (_, ci) => (ri === 0 ? `Header ${ci + 1}` : ''))
    );
  }

  const handleRowsChange = useCallback((val: string) => {
    const n = Math.max(2, Math.min(20, parseInt(val) || 2)); // min 2: header + 1 data row
    setRows(n);
    setCells((prev) => {
      const newGrid = buildGrid(n, cols);
      for (let r = 0; r < Math.min(n, prev.length); r++) {
        for (let c = 0; c < Math.min(cols, prev[r].length); c++) {
          newGrid[r][c] = prev[r][c];
        }
      }
      return newGrid;
    });
  }, [cols]);

  const handleColsChange = useCallback((val: string) => {
    const n = Math.max(1, Math.min(10, parseInt(val) || 1));
    setCols(n);
    setCells((prev) => {
      const newGrid = buildGrid(rows, n);
      for (let r = 0; r < Math.min(rows, prev.length); r++) {
        for (let c = 0; c < Math.min(n, prev[r]?.length ?? 0); c++) {
          newGrid[r][c] = prev[r][c];
        }
      }
      return newGrid;
    });
  }, [rows]);

  const updateCell = (r: number, c: number, val: string) => {
    setCells((prev) => {
      const copy = prev.map((row) => [...row]);
      copy[r][c] = val;
      return copy;
    });
  };

  const handleInsert = () => {
    if (cells.length < 2 || cells[0].length < 1) return;
    const headerRow = `| ${cells[0].map((h) => h || ' ').join(' | ')} |`;
    const separator = `| ${cells[0].map(() => '---').join(' | ')} |`;
    const dataRows = cells.slice(1).map((row) => `| ${row.map((c) => c || ' ').join(' | ')} |`);
    const md = [headerRow, separator, ...dataRows].join('\n');
    onInsert('\n' + md + '\n');
    onClose();
    // Reset
    setRows(3);
    setCols(3);
    setCells(buildGrid(3, 3));
  };

  return (
    <DialogShell isOpen={isOpen} onClose={onClose} title="Insert Table" icon={<Table2 className="h-4 w-4 text-indigo-600" />} width="w-[560px]">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <InputField label="Rows (incl. header)" value={String(rows)} onChange={handleRowsChange} type="number" />
          <InputField label="Columns" value={String(cols)} onChange={handleColsChange} type="number" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">Table Content</label>
          <div className="overflow-auto max-h-60 border border-gray-200 ">
            <table className="w-full text-xs">
              <tbody>
                {cells.map((row, ri) => (
                  <tr key={ri} className={ri === 0 ? 'bg-indigo-50' : ri % 2 === 0 ? 'bg-gray-50' : ''}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="p-0.5">
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) => updateCell(ri, ci, e.target.value)}
                          placeholder={ri === 0 ? `Header ${ci + 1}` : ''}
                          className={`w-full px-2 py-1 text-xs border border-gray-200  focus:outline-none focus:ring-1 focus:ring-indigo-500 ${ri === 0 ? 'font-semibold' : ''}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <DialogActions onClose={onClose} onInsert={handleInsert} insertLabel="Insert Table" />
      </div>
    </DialogShell>
  );
};
